import { denormalize, normalize, schema } from 'normalizr';
import { cloneDeep } from 'src/ep-utils';
import { UserDefinedParentType } from '../user-defined/user-defined-object.types';
import {
    ArticleNodeType,
    EntityNodeType,
    PartyNodeType,
    SubArticleNodeType,
    TransactionNodeType
} from './contract-object.types';

// put ETMRequest and ETMReply in RootSchema too

const addressSchema = new schema.Entity('Address', {}, { idAttribute: 'Address_ID' });

const phoneSchema = new schema.Entity('Phone', {}, { idAttribute: 'Phone_ID' });

const addNodeTypeProcessStrategy = (
    value: any,
    nodeType: TransactionNodeType | ArticleNodeType | SubArticleNodeType | EntityNodeType | PartyNodeType
) => {
    let copy = cloneDeep(value); //TODO: do shallow copy with spread(...) ?
    let localParent = { ...parent };
    copy.nodeType = nodeType;
    return copy;
};

const addUserDefinedTableProcessStrategy = (value: any, parentObjectCode: UserDefinedParentType) => {
    let copy = cloneDeep(value);
    copy.Parent_Object_Code = parentObjectCode;
    //copy.nodeType = value.Table_Name; //NEEDED FOR STATE VALIDATION?????
    return copy;
};

const riskGroupSchema = new schema.Entity(
    'RiskGroup',
    {},
    {
        idAttribute: 'RiskGroup_ID'
    }
);

const subLocationSchema = new schema.Entity(
    'SubLocation',
    {},
    {
        idAttribute: 'Sub_Loc_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Loc_ID = parent.Loc_ID;
            return copy;
        }
    }
);

const locationSchema = new schema.Entity(
    'Location',
    {
        Sub_Location: [subLocationSchema]
    },
    {
        idAttribute: 'Loc_ID'
    }
);

const LossHistorySchema = new schema.Entity(
    'LossHistory',
    {},
    {
        idAttribute: 'LossHist_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.PriorHist_ID = parent.PriorHist_ID;
            return copy;
        }
    }
);

const priorHistorySchema = new schema.Entity(
    'PriorHistory',
    {
        Loss_Summary_Detail: [LossHistorySchema]
    },
    {
        idAttribute: 'PriorHist_ID'
    }
);

const productSchema = new schema.Entity(
    'Product',
    {},
    {
        idAttribute: 'Cost_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Article_ID = parent.Article_ID;
            return copy;
        }
    }
);

const thirdPartySchema = new schema.Entity(
    'Entity',
    {
        Addresses: {
            Address: [addressSchema]
        },
        Phones: {
            Phone: [phoneSchema]
        }
    },
    {
        idAttribute: 'Entity_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Interest_ID = parent.Interest_ID;
            return addNodeTypeProcessStrategy(copy, EntityNodeType.Third_Party);
        }
    }
);

const interestSchema = new schema.Entity(
    'Interest',
    {
        Interest_Name: thirdPartySchema
    },
    {
        idAttribute: 'Interest_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            if (parent.Article_ID) {
                copy.Ref_Type = 'Article';
                copy.Ref_Article_ID = parent.Article_ID;
            } else {
                copy.Ref_Type = 'Contract';
            }
            return copy;
        }
    }
);

const cdaaDriverVehicleSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.DVH_SubArticle_ID + '-' + SubArticleNodeType.CDAA_Driver_Vehicle;
        },
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Article_ID = parent.Article_ID;
            return addNodeTypeProcessStrategy(copy, SubArticleNodeType.CDAA_Driver_Vehicle);
        }
    }
);

const cdaaDriverViolationSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.DVI_SubArticle_ID + '-' + SubArticleNodeType.CDAA_Driver_Violation;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDAA_Driver_Violation);
        }
    }
);

const cdglClassProductSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.GLP_SubArticle_ID + '-' + SubArticleNodeType.CDGL_Class_Product;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDGL_Class_Product);
        }
    }
);

const cdmmIndividualScheduleSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.Indiv_SubArticle_ID + '-' + SubArticleNodeType.CDMM_Individual_Schedule;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDMM_Individual_Schedule);
        }
    }
);

const cdaaDriverDiscountSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.DDS_SubArticle_ID + '-' + SubArticleNodeType.CDAA_Driver_Discount;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDAA_Driver_Discount);
        }
    }
);

const cdaaDriverFinRespSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.DFR_SubArticle_ID + '-' + SubArticleNodeType.CDAA_Driver_FinResp;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDAA_Driver_FinResp);
        }
    }
);

const cdacFilingDetailSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.FIL_SubArticle_ID + '-' + SubArticleNodeType.CDAC_Filing_Detail;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDAC_Filing_Detail);
        }
    }
);

const cdpcUnderwritingProtectSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.PTI_SubArticle_ID + '-' + SubArticleNodeType.CDPC_Underwriting_Protect;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDPC_Underwriting_Protect);
        }
    }
);

const cdacTruckTerminalSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.TTM_SubArticle_ID + '-' + SubArticleNodeType.CDAC_Truck_Terminal;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDAC_Truck_Terminal);
        }
    }
);

const cduxPrimarySchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.PRI_SubArticle_ID + '-' + SubArticleNodeType.CDUX_Primary;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDUX_Primary);
        }
    }
);

const cduxVehicleSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.VEH_SubArticle_ID + '-' + SubArticleNodeType.CDUX_Vehicle;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDUX_Vehicle);
        }
    }
);

const cduxWatercraftSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.WAT_SubArticle_ID + '-' + SubArticleNodeType.CDUX_Watercraft;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDUX_Watercraft);
        }
    }
);

const cdimCommunicationsTowerantennaSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.CTA_SubArticle_ID + '-' + SubArticleNodeType.CDIM_Communications_TowerAntenna;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDIM_Communications_TowerAntenna);
        }
    }
);

const cdimScheduleItemsSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.CIM_SubArticle_ID + '-' + SubArticleNodeType.CDIM_Schedule_Items;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDIM_Schedule_Items);
        }
    }
);

const cdimTransportationDetailSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.TRD_SubArticle_ID + '-' + SubArticleNodeType.CDIM_Transportation_Detail;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDIM_Transportation_Detail);
        }
    }
);

const cdimBuildersRiskRiggingItemSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.BRI_SubArticle_ID + '-' + SubArticleNodeType.CDIM_Builders_Risk_Rigging_Item;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDIM_Builders_Risk_Rigging_Item);
        }
    }
);

const cdcrEmployeesSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.CRE_SubArticle_ID + '-' + SubArticleNodeType.CDCR_Employees;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDCR_Employees);
        }
    }
);

const cdcrMoneySecuritiesSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.CRS_SubArticle_ID + '-' + SubArticleNodeType.CDCR_Money_Securities;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDCR_Money_Securities);
        }
    }
);

const cdcrSafeVaultInfoSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.CRV_SubArticle_ID + '-' + SubArticleNodeType.CDCR_Safe_Vault_Info;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDCR_Safe_Vault_Info);
        }
    }
);

const cdjbRatingExhibitionSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.JBE_SubArticle_ID + '-' + SubArticleNodeType.CDJB_Rating_Exhibition;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDJB_Rating_Exhibition);
        }
    }
);

const cdjbRatingOffPremisesSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.JBO_SubArticle_ID + '-' + SubArticleNodeType.CDJB_Rating_Off_Premises;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDJB_Rating_Off_Premises);
        }
    }
);

const cdjbUnderwritingProtectSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.JBP_SubArticle_ID + '-' + SubArticleNodeType.CDJB_Underwriting_Protect;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDJB_Underwriting_Protect);
        }
    }
);

const cdppItemSchema = new schema.Entity(
    'SubArticle',
    {},
    {
        idAttribute: (value) => {
            return value.PPI_SubArticle_ID + '-' + SubArticleNodeType.CDPP_Item;
        },
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, SubArticleNodeType.CDPP_Item);
        }
    }
);

const lineWideSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.Line_Wide);
        }
    }
);

const cdaaVehicleSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAA_Vehicle);
        }
    }
);

const cdaaDriverSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDAA_Driver_Vehicle: [cdaaDriverVehicleSchema],
        CDAA_Driver_Violation: [cdaaDriverViolationSchema],
        CDAA_Driver_Discount: [cdaaDriverDiscountSchema],
        CDAA_Driver_FinResp: [cdaaDriverFinRespSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAA_Driver);
        }
    }
);

const cdacUnderwritingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAC_Underwriting);
        }
    }
);

const cdacFilingSchema = new schema.Entity(
    'Article',
    {
        CDAC_Filing_Detail: [cdacFilingDetailSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAC_Filing);
        }
    }
);

const cdacAutoSymbolSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAC_Auto_Symbol);
        }
    }
);

const cdacStateRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAC_State_Rating);
        }
    }
);

const cdacTruckEquipmentSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAC_Truck_Equipment);
        }
    }
);

const cdacTruckInterchangeSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAC_Truck_Interchange);
        }
    }
);

const cdacTruckZoneSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDAC_Truck_Terminal: [cdacTruckTerminalSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAC_Truck_Zone);
        }
    }
);

const cdapAllRiskSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAP_All_Risk);
        }
    }
);

const cdapUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAP_Underwriting);
        }
    }
);

const cdglClassRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDGL_Class_Product: [cdglClassProductSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDGL_Class_Rating);
        }
    }
);

const cdglStateRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDGL_State_Rating);
        }
    }
);

const cdglClaimsMadeSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDGL_Claims_Made);
        }
    }
);

const cdglUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDGL_Underwriting);
        }
    }
);

const cdmaMiscArticleSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDMA_Misc_Article);
        }
    }
);

const cdmaERPSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDMA_ERP);
        }
    }
);

const cdplRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPL_Rating);
        }
    }
);

const cdplIndividualsSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPL_Individuals);
        }
    }
);

const cdplUnderwritingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPL_Underwriting);
        }
    }
);

const cdmmFacilitySchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDMM_Individual_Schedule: [cdmmIndividualScheduleSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDMM_Facility);
        }
    }
);

const cdmmUnderwritingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDMM_Underwriting);
        }
    }
);

const cdmmIndividualSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDMM_Individual);
        }
    }
);

const cdamAnimalSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDAM_Animal);
        }
    }
);

const cddoRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDDO_Rating);
        }
    }
);

const cdepRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDEP_Rating);
        }
    }
);

const cdcrRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDCR_Employees: [cdcrEmployeesSchema],
        CDCR_Money_Securities: [cdcrMoneySecuritiesSchema],
        CDCR_Safe_Vault_Info: [cdcrSafeVaultInfoSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDCR_Rating);
        }
    }
);

const cdeoJobSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDEO_Job);
        }
    }
);

const cdeoRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDEO_Rating);
        }
    }
);

const cdeoUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDEO_Underwriting);
        }
    }
);

const cdhmCplSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDHM_CPL);
        }
    }
);

const cdhmHomeownersSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDHM_Homeowners);
        }
    }
);

const cdhmMobilehomeSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDHM_Mobile_Home);
        }
    }
);

const cdimAccountsReceivableSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_Accounts_Receivable);
        }
    }
);

const cdimBuildersRiskSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDIM_Builders_Risk_Rigging_Item: [cdimBuildersRiskRiggingItemSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_Builders_Risk);
        }
    }
);

const cdimBuildersRiskUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_Builders_Risk_Underwriting);
        }
    }
);

const cdimCommunicationsSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDIM_Communications_TowerAntenna: [cdimCommunicationsTowerantennaSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_Communications);
        }
    }
);

const cdimEdpSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_EDP);
        }
    }
);

const cdimEdpUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_EDP_Underwriting);
        }
    }
);

const cdimEquipmentFloaterSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_Equipment_Floater);
        }
    }
);

const cdimScheduleSummarySchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDIM_Schedule_Items: [cdimScheduleItemsSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_Schedule_Summary);
        }
    }
);

const cdimSubcontractorUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_Subcontractor_Underwriting);
        }
    }
);

const cdimTransportationSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDIM_Transportation_Detail: [cdimTransportationDetailSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_Transportation);
        }
    }
);

const cdimTransportationRiggingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDIM_Builders_Risk_Rigging_Item: [cdimBuildersRiskRiggingItemSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDIM_Transportation_Rigging);
        }
    }
);

const cdgkRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDGK_Rating);
        }
    }
);

const cdgdPhysDamLocSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDGD_PhysDam_Loc);
        }
    }
);

const cdgkUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDGK_Underwriting);
        }
    }
);

const cdjbIndividualsSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDJB_Individuals);
        }
    }
);

const cdjbRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDJB_Rating_Exhibition: [cdjbRatingExhibitionSchema],
        CDJB_Rating_Off_Premises: [cdjbRatingOffPremisesSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDJB_Rating);
        }
    }
);

const cdjbUnderwritingSchema = new schema.Entity(
    'Article',
    { CDJB_Underwriting_Protect: [cdjbUnderwritingProtectSchema] },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDJB_Underwriting);
        }
    }
);

const cdgdRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDGD_Rating);
        }
    }
);

const cdgdUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDGD_Underwriting);
        }
    }
);

const cduxCareCustodyControlSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDUX_Care_Custody_Control);
        }
    }
);

const cduxLocationSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDUX_Location);
        }
    }
);

const cduxUnderlyingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDUX_Primary: [cduxPrimarySchema],
        CDUX_Vehicle: [cduxVehicleSchema],
        CDUX_Watercraft: [cduxWatercraftSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDUX_Underlying);
        }
    }
);

const cdpcBlanketRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPC_Blanket_Rating);
        }
    }
);

const cdpcClassRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPC_Class_Rating);
        }
    }
);

const cdpcGlassScheduleSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPC_Glass_Schedule);
        }
    }
);

const cdpcGlassUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPC_Glass_Underwriting);
        }
    }
);

const cdpcUnderwritingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDPC_Underwriting_Protect: [cdpcUnderwritingProtectSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPC_Underwriting);
        }
    }
);

const cdpcUnderwritingLocationSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPC_Underwriting_Location);
        }
    }
);

const cdpoRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPO_Rating);
        }
    }
);

const cdpxRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPX_Rating);
        }
    }
);

const cdsbRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDSB_Rating);
        }
    }
);

const cdllRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDLL_Rating);
        }
    }
);

const cdllUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDLL_Underwriting);
        }
    }
);

const cdppScheduleSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema],
        CDPP_Item: [cdppItemSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDPP_Schedule);
        }
    }
);

const cdwcClassRatingSchema = new schema.Entity(
    'Article',
    {
        Product: [productSchema],
        Interest: [interestSchema]
    },
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDWC_Class_Rating);
        }
    }
);

const cdwcIndividualSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDWC_Individual);
        }
    }
);

const cdwcOtherStateSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDWC_Other_State);
        }
    }
);

const cdwcStateRatingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDWC_State_Rating);
        }
    }
);

const cdwcUnderwritingSchema = new schema.Entity(
    'Article',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return addNodeTypeProcessStrategy(copy, ArticleNodeType.CDWC_Underwriting);
        }
    }
);

const brokerPartySchema = new schema.Entity(
    'Party',
    {},
    {
        idAttribute: 'Party_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            copy.Line_Code = parent.Line_Code;
            return addNodeTypeProcessStrategy(copy, PartyNodeType.Contract_Broker);
        }
    }
);

const supplierPartySchema = new schema.Entity(
    'Party',
    {},
    {
        idAttribute: 'Party_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            copy.Line_Code = parent.Line_Code;
            return addNodeTypeProcessStrategy(copy, PartyNodeType.Contract_Supplier);
        }
    }
);

const producerPartySchema = new schema.Entity(
    'Party',
    {},
    {
        idAttribute: 'Party_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            copy.Line_Code = parent.Line_Code;
            return addNodeTypeProcessStrategy(copy, PartyNodeType.Contract_Producer);
        }
    }
);

const financeCoPartySchema = new schema.Entity(
    'Party',
    {},
    {
        idAttribute: 'Party_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            copy.Line_Code = parent.Line_Code;
            return addNodeTypeProcessStrategy(copy, PartyNodeType.Contract_FinanceCo);
        }
    }
);

const teamMemberSchema = new schema.Entity(
    'TeamMember',
    {},
    {
        idAttribute: 'Party_ID'
    }
);

const attachmentSchema = new schema.Entity(
    'Attachment',
    {},
    {
        idAttribute: 'Attachment_ID'
    }
);

const formSchema = new schema.Entity(
    'Form',
    {},
    {
        idAttribute: 'Article_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return copy;
        }
    }
);

const factorSchema = new schema.Entity(
    'Factor',
    {},
    {
        idAttribute: 'Factor_ID',
        processStrategy: (input, parent, key) => {
            let copy = cloneDeep(input);
            copy.Line_ID = parent.Line_ID;
            return copy;
        }
    }
);

const conditionSchema = new schema.Entity(
    'Condition',
    {},
    {
        idAttribute: 'Condition_ID'
    }
);

const nameSchema = new schema.Entity(
    'Name',
    {},
    {
        idAttribute: 'Name_ID'
    }
);

const profileSchema = new schema.Entity(
    'Profile',
    {},
    {
        idAttribute: 'Profile_ID'
    }
);

const lineSchema = new schema.Entity(
    'Line',
    {
        Contract_Party: {
            Contract_Broker: [brokerPartySchema],
            Contract_Supplier: [supplierPartySchema],
            Contract_Producer: [producerPartySchema],
            Contract_FinanceCo: [financeCoPartySchema]
        },
        Contract_Detail: {
            CDAA_Vehicle: [cdaaVehicleSchema],
            CDAA_Driver: [cdaaDriverSchema],
            CDAC_Filing: [cdacFilingSchema],
            CDAC_Auto_Symbol: [cdacAutoSymbolSchema],
            CDAC_State_Rating: [cdacStateRatingSchema],
            CDAC_Underwriting: [cdacUnderwritingSchema],
            CDAC_Truck_Equipment: [cdacTruckEquipmentSchema],
            CDAC_Truck_Interchange: [cdacTruckInterchangeSchema],
            CDAC_Truck_Zone: [cdacTruckZoneSchema],
            CDAP_All_Risk: [cdapAllRiskSchema],
            CDAP_Underwriting: [cdapUnderwritingSchema],
            CDGL_Class_Rating: [cdglClassRatingSchema],
            CDGL_State_Rating: [cdglStateRatingSchema],
            CDGL_Claims_Made: [cdglClaimsMadeSchema],
            CDGL_Underwriting: [cdglUnderwritingSchema],
            CDMA_Misc_Article: [cdmaMiscArticleSchema],
            CDMA_ERP: [cdmaERPSchema],
            CDPL_Rating: [cdplRatingSchema],
            CDPL_Individuals: [cdplIndividualsSchema],
            CDPL_Underwriting: [cdplUnderwritingSchema],
            CDMM_Facility: [cdmmFacilitySchema],
            CDMM_Underwriting: [cdmmUnderwritingSchema],
            CDMM_Individual: [cdmmIndividualSchema],
            CDAM_Animal: [cdamAnimalSchema],
            CDDO_Rating: [cddoRatingSchema],
            CDEP_Rating: [cdepRatingSchema],
            CDCR_Rating: [cdcrRatingSchema],
            CDEO_Job: [cdeoJobSchema],
            CDEO_Rating: [cdeoRatingSchema],
            CDEO_Underwriting: [cdeoUnderwritingSchema],
            CDHM_CPL: [cdhmCplSchema],
            CDHM_Homeowners: [cdhmHomeownersSchema],
            CDHM_Mobile_Home: [cdhmMobilehomeSchema],
            CDIM_Accounts_Receivable: [cdimAccountsReceivableSchema],
            CDIM_Builders_Risk: [cdimBuildersRiskSchema],
            CDIM_Builders_Risk_Underwriting: [cdimBuildersRiskUnderwritingSchema],
            CDIM_Communications: [cdimCommunicationsSchema],
            CDIM_EDP: [cdimEdpSchema],
            CDIM_EDP_Underwriting: [cdimEdpUnderwritingSchema],
            CDIM_Equipment_Floater: [cdimEquipmentFloaterSchema],
            CDIM_Schedule_Summary: [cdimScheduleSummarySchema],
            CDIM_Subcontractor_Underwriting: [cdimSubcontractorUnderwritingSchema],
            CDIM_Transportation: [cdimTransportationSchema],
            CDIM_Transportation_Rigging: [cdimTransportationRiggingSchema],
            CDGK_Rating: [cdgkRatingSchema],
            CDGD_PhysDam_Loc: [cdgdPhysDamLocSchema],
            CDGK_Underwriting: [cdgkUnderwritingSchema],
            CDJB_Individuals: [cdjbIndividualsSchema],
            CDJB_Rating: [cdjbRatingSchema],
            CDJB_Underwriting: [cdjbUnderwritingSchema],
            CDGD_Rating: [cdgdRatingSchema],
            CDGD_Underwriting: [cdgdUnderwritingSchema],
            CDUX_Care_Custody_Control: [cduxCareCustodyControlSchema],
            CDUX_Location: [cduxLocationSchema],
            CDUX_Underlying: [cduxUnderlyingSchema],
            CDPC_Blanket_Rating: [cdpcBlanketRatingSchema],
            CDPC_Class_Rating: [cdpcClassRatingSchema],
            CDPC_Glass_Schedule: [cdpcGlassScheduleSchema],
            CDPC_Glass_Underwriting: [cdpcGlassUnderwritingSchema],
            CDPC_Underwriting: [cdpcUnderwritingSchema],
            CDPC_Underwriting_Location: [cdpcUnderwritingLocationSchema],
            CDPO_Rating: [cdpoRatingSchema],
            CDPX_Rating: [cdpxRatingSchema],
            CDSB_Rating: [cdsbRatingSchema],
            CDLL_Rating: [cdllRatingSchema],
            CDLL_Underwriting: [cdllUnderwritingSchema],
            CDPP_Schedule: [cdppScheduleSchema],
            CDWC_Class_Rating: [cdwcClassRatingSchema],
            CDWC_Individual: [cdwcIndividualSchema],
            CDWC_Other_State: [cdwcOtherStateSchema],
            CDWC_State_Rating: [cdwcStateRatingSchema],
            CDWC_Underwriting: [cdwcUnderwritingSchema]
        },
        Line_Wide_Product: [lineWideSchema],
        Contract_Form: [formSchema],
        Contract_Factor: [factorSchema]
    },
    {
        idAttribute: 'Line_ID'
    }
);

const entityUserDefinedTableSchema = new schema.Entity(
    'UDTable',
    {},
    {
        idAttribute: 'Table_Name',
        processStrategy: (input, parent, key) => {
            return addUserDefinedTableProcessStrategy(input, UserDefinedParentType.Entity);
        }
    }
);

const contractUserDefinedTableSchema = new schema.Entity(
    'UDTable',
    {},
    {
        idAttribute: 'Table_Name',
        processStrategy: (input, parent, key) => {
            return addUserDefinedTableProcessStrategy(input, UserDefinedParentType.Contract);
        }
    }
);

const consumerSchema = new schema.Entity(
    'Entity',
    {
        Addresses: {
            Address: [addressSchema]
        },
        Phones: {
            Phone: [phoneSchema]
        },
        UD_Table: [entityUserDefinedTableSchema]
    },
    {
        idAttribute: 'Entity_ID',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, EntityNodeType.Consumer);
        }
    }
);

const contractSchema = new schema.Entity(
    'Contract',
    {
        Consumer: consumerSchema,
        Contract_Location: [locationSchema],
        RiskGroups: { RiskGroup: [riskGroupSchema] },
        Contract_Line: [lineSchema],
        Team_Members: { Team_Member: [teamMemberSchema] },
        Contract_Interest: [interestSchema],
        Contract_Condition: [conditionSchema],
        Contract_Name: [nameSchema],
        Contract_Profile: [profileSchema],
        Contract_Attachment: [attachmentSchema],
        Prior_History: [priorHistorySchema],
        UD_Table: [contractUserDefinedTableSchema]
    },
    { idAttribute: 'Contract_ID' }
);

const contractGetSchema = new schema.Entity(
    'Transaction',
    {
        Contract: contractSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Contract);
        }
    }
);
//put in Transaction entity or ContractGet entity???
//do we need to set an ID other than one for cases where we use multi-tran?   
//  OR will we split multi-tran differenty since each tran could reference the same objects which would overwrite entities

const contractProcessSchema = new schema.Entity(
    'Transaction',
    {
        Contract: contractSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Contract);
        }
    }
);

const applicationSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const applicationGetSchema = new schema.Entity(
    'Transaction',
    {
        Application: applicationSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Application);
        }
    }
);

const applicationProcessSchema = new schema.Entity(
    'Transaction',
    {
        Application: applicationSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Application);
        }
    }
);

const bindSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const bindGetSchema = new schema.Entity(
    'Transaction',
    {
        Bind: bindSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Bind);
        }
    }
);

const bindTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Bind: bindSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Bind);
        }
    }
);

const bindProcessSchema = new schema.Entity(
    'Transaction',
    {
        Bind: bindSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Bind);
        }
    }
);

const issueSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const endorsementSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const quoteSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const marketSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const scheduleCancelSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const cancelSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const reinstateSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const selfAuditSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const auditSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const extendTermSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const renewalSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const reissueSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const rewriteSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const disableSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const changeConsumerSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const nonRenewSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const contractRateSchema = new schema.Entity(
    'Procedure',
    {
        Contract: contractSchema
    },
    { idAttribute: 'Proc_ID' }
);

const issueGetSchema = new schema.Entity(
    'Transaction',
    {
        Issue: issueSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Issue);
        }
    }
);

const issueTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Issue: issueSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Issue);
        }
    }
);

const issueProcessSchema = new schema.Entity(
    'Transaction',
    {
        Issue: issueSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Issue);
        }
    }
);

const quoteGetSchema = new schema.Entity(
    'Transaction',
    {
        Quote: quoteSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Quote);
        }
    }
);

const quoteTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Quote: quoteSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Quote);
        }
    }
);

const quoteProcessSchema = new schema.Entity(
    'Transaction',
    {
        Quote: quoteSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Quote);
        }
    }
);

const marketGetSchema = new schema.Entity(
    'Transaction',
    {
        Market: marketSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Market);
        }
    }
);

const marketTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Market: marketSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Market);
        }
    }
);

const marketProcessSchema = new schema.Entity(
    'Transaction',
    {
        Market: marketSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Market);
        }
    }
);

const endorsementGetSchema = new schema.Entity(
    'Transaction',
    {
        Endorsement: endorsementSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Endorsement);
        }
    }
);

const endorsementTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Endorsement: endorsementSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Endorsement);
        }
    }
);

const endorsementProcessSchema = new schema.Entity(
    'Transaction',
    {
        Endorsement: endorsementSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Endorsement);
        }
    }
);

const scheduleCancelTemplateSchema = new schema.Entity(
    'Transaction',
    {
        ScheduleCancel: scheduleCancelSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ScheduleCancel);
        }
    }
);

const scheduleCancelGetSchema = new schema.Entity(
    'Transaction',
    {
        ScheduleCancel: scheduleCancelSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ScheduleCancel);
        }
    }
);

const scheduleCancelProcessSchema = new schema.Entity(
    'Transaction',
    {
        ScheduleCancel: scheduleCancelSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ScheduleCancel);
        }
    }
);

const cancelTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Cancel: cancelSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Cancel);
        }
    }
);

const cancelGetSchema = new schema.Entity(
    'Transaction',
    {
        Cancel: cancelSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Cancel);
        }
    }
);

const cancelProcessSchema = new schema.Entity(
    'Transaction',
    {
        Cancel: cancelSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Cancel);
        }
    }
);

const reinstateTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Reinstate: reinstateSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Reinstate);
        }
    }
);

const reinstateGetSchema = new schema.Entity(
    'Transaction',
    {
        Reinstate: reinstateSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Reinstate);
        }
    }
);

const reinstateProcesschema = new schema.Entity(
    'Transaction',
    {
        Reinstate: reinstateSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Reinstate);
        }
    }
);

const selfAuditTemplateSchema = new schema.Entity(
    'Transaction',
    {
        SelfAudit: selfAuditSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.SelfAudit);
        }
    }
);

const selfAuditGetSchema = new schema.Entity(
    'Transaction',
    {
        SelfAudit: selfAuditSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.SelfAudit);
        }
    }
);

const selfAuditProcessSchema = new schema.Entity(
    'Transaction',
    {
        SelfAudit: selfAuditSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.SelfAudit);
        }
    }
);

const auditTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Audit: auditSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Audit);
        }
    }
);

const auditGetSchema = new schema.Entity(
    'Transaction',
    {
        Audit: auditSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Audit);
        }
    }
);

const auditProcessSchema = new schema.Entity(
    'Transaction',
    {
        Audit: auditSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Audit);
        }
    }
);

const extendTermGetSchema = new schema.Entity(
    'Transaction',
    {
        ExtendTerm: extendTermSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ExtendTerm);
        }
    }
);

const extendTermTemplateSchema = new schema.Entity(
    'Transaction',
    {
        ExtendTerm: extendTermSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ExtendTerm);
        }
    }
);

const extendTermProcessSchema = new schema.Entity(
    'Transaction',
    {
        ExtendTerm: extendTermSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ExtendTerm);
        }
    }
);

const renewalGetSchema = new schema.Entity(
    'Transaction',
    {
        Renewal: renewalSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Renewal);
        }
    }
);

const renewalTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Renewal: renewalSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Renewal);
        }
    }
);

const renewalProcessSchema = new schema.Entity(
    'Transaction',
    {
        Renewal: renewalSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Renewal);
        }
    }
);

const reissueTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Reissue: reissueSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Reissue);
        }
    }
);

const reissueGetSchema = new schema.Entity(
    'Transaction',
    {
        Reissue: reissueSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Reissue);
        }
    }
);

const reissueProcessSchema = new schema.Entity(
    'Transaction',
    {
        Reissue: reissueSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Reissue);
        }
    }
);

const rewriteTemplateSchema = new schema.Entity(
    'Transaction',
    {
        Rewrite: rewriteSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Rewrite);
        }
    }
);

const rewriteGetSchema = new schema.Entity(
    'Transaction',
    {
        Rewrite: rewriteSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Rewrite);
        }
    }
);

const rewriteProcessSchema = new schema.Entity(
    'Transaction',
    {
        Rewrite: rewriteSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.Rewrite);
        }
    }
);

const contractDisableTemplateSchema = new schema.Entity(
    'Transaction',
    {
        ContractDisable: disableSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ContractDisable);
        }
    }
);

const contractDisableGetSchema = new schema.Entity(
    'Transaction',
    {
        ContractDisable: disableSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ContractDisable);
        }
    }
);

const contractDisableProcessSchema = new schema.Entity(
    'Transaction',
    {
        ContractDisable: disableSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ContractDisable);
        }
    }
);

const changeConsumerTemplateSchema = new schema.Entity(
    'Transaction',
    {
        ChangeConsumer: changeConsumerSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ChangeConsumer);
        }
    }
);

const changeConsumerGetSchema = new schema.Entity(
    'Transaction',
    {
        ChangeConsumer: changeConsumerSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ChangeConsumer);
        }
    }
);

const changeConsumerProcessSchema = new schema.Entity(
    'Transaction',
    {
        ChangeConsumer: changeConsumerSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ChangeConsumer);
        }
    }
);

const nonRenewTemplateSchema = new schema.Entity(
    'Transaction',
    {
        NonRenew: nonRenewSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.NonRenew);
        }
    }
);

const nonRenewGetSchema = new schema.Entity(
    'Transaction',
    {
        NonRenew: nonRenewSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.NonRenew);
        }
    }
);

const nonRenewProcessSchema = new schema.Entity(
    'Transaction',
    {
        NonRenew: nonRenewSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.NonRenew);
        }
    }
);

const contractRateTemplateSchema = new schema.Entity(
    'Transaction',
    {
        ContractRate: contractRateSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ContractRate);
        }
    }
);

const contractRateGetSchema = new schema.Entity(
    'Transaction',
    {
        ContractRate: contractRateSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ContractRate);
        }
    }
);

const contractRateProcessSchema = new schema.Entity(
    'Transaction',
    {
        ContractRate: contractRateSchema
    },
    {
        idAttribute: (value) => '0',
        processStrategy: (input, parent, key) => {
            return addNodeTypeProcessStrategy(input, TransactionNodeType.ContractRate);
        }
    }
);

const conceptOneRequestSchema = new schema.Entity(
    'Request',
    {
        ContractGet: contractGetSchema,
        ContractProcess: contractProcessSchema,
        ApplicationGet: applicationGetSchema,
        ApplicationProcess: applicationProcessSchema,
        BindGet: bindGetSchema,
        BindProcess: bindProcessSchema,
        BindTemplate: bindTemplateSchema,
        IssueGet: issueGetSchema,
        IssueProcess: issueProcessSchema,
        IssueTemplate: issueTemplateSchema,
        QuoteGet: quoteGetSchema,
        QuoteProcess: quoteProcessSchema,
        QuoteTemplate: quoteTemplateSchema,
        MarketGet: marketGetSchema,
        MarketTemplate: marketTemplateSchema,
        MarketProcess: marketProcessSchema,
        EndorsementGet: endorsementGetSchema,
        EndorsementProcess: endorsementProcessSchema,
        EndorsementTemplate: endorsementTemplateSchema,
        ScheduleCancelGet: scheduleCancelGetSchema,
        ScheduleCancelTemplate: scheduleCancelTemplateSchema,
        ScheduleCancelProcess: scheduleCancelProcessSchema,
        CancelGet: cancelGetSchema,
        CancelTemplate: cancelTemplateSchema,
        CancelProcess: cancelProcessSchema,
        ReinstateTemplate: reinstateTemplateSchema,
        ReinstateGet: reinstateGetSchema,
        ReinstateProcess: reinstateProcesschema,
        SelfAuditGet: selfAuditGetSchema,
        SelfAuditTemplate: selfAuditTemplateSchema,
        SelfAuditProcess: selfAuditProcessSchema,
        AuditGet: auditGetSchema,
        AuditTemplate: auditTemplateSchema,
        AuditProcess: auditProcessSchema,
        ExtendTermGet: extendTermGetSchema,
        ExtendTermTemplate: extendTermTemplateSchema,
        ExtendTermProcess: extendTermProcessSchema,
        RenewalGet: renewalGetSchema,
        RenewalTemplate: renewalTemplateSchema,
        RenewalProcess: renewalProcessSchema,
        ReissueGet: reissueGetSchema,
        ReissueTemplate: reissueTemplateSchema,
        ReissueProcess: reissueProcessSchema,
        RewriteGet: rewriteGetSchema,
        RewriteTemplate: rewriteTemplateSchema,
        RewriteProcess: rewriteProcessSchema,
        ContractDisableGet: contractDisableGetSchema,
        ContractDisableTemplate: contractDisableTemplateSchema,
        ContractDisableProcess: contractDisableProcessSchema,
        ChangeConsumerTemplate: changeConsumerTemplateSchema,
        ChangeConsumerGet: changeConsumerGetSchema,
        ChangeConsumerProcess: changeConsumerProcessSchema,
        NonRenewTemplate: nonRenewTemplateSchema,
        NonRenewGet: nonRenewGetSchema,
        NonRenewProcess: nonRenewProcessSchema,
        ContractRateTemplate: contractRateTemplateSchema,
        ContractRateGet: contractRateGetSchema,
        ContractRateProcess: contractRateProcessSchema
    },
    {
        idAttribute: (value) => '0'
        //processStrategy: (input, parent, key) => { return addNodeTypeProcessStrategy(input, "ConceptOneRequest") }
    }
);

const conceptOneReplySchema = new schema.Entity(
    'Reply',
    {
        ContractGet: contractGetSchema,
        ContractProcess: contractProcessSchema,
        ApplicationGet: applicationGetSchema,
        ApplicationProcess: applicationProcessSchema,
        BindGet: bindGetSchema,
        BindProcess: bindProcessSchema,
        BindTemplate: bindTemplateSchema,
        IssueGet: issueGetSchema,
        IssueProcess: issueProcessSchema,
        IssueTemplate: issueTemplateSchema,
        QuoteGet: quoteGetSchema,
        QuoteProcess: quoteProcessSchema,
        QuoteTemplate: quoteTemplateSchema,
        MarketGet: marketGetSchema,
        MarketTemplate: marketTemplateSchema,
        MarketProcess: marketProcessSchema,
        EndorsementGet: endorsementGetSchema,
        EndorsementProcess: endorsementProcessSchema,
        EndorsementTemplate: endorsementTemplateSchema,
        ScheduleCancelGet: scheduleCancelGetSchema,
        ScheduleCancelTemplate: scheduleCancelTemplateSchema,
        ScheduleCancelProcess: scheduleCancelProcessSchema,
        CancelGet: cancelGetSchema,
        CancelTemplate: cancelTemplateSchema,
        CancelProcess: cancelProcessSchema,
        ReinstateTemplate: reinstateTemplateSchema,
        ReinstateGet: reinstateGetSchema,
        ReinstateProcess: reinstateProcesschema,
        SelfAuditGet: selfAuditGetSchema,
        SelfAuditTemplate: selfAuditTemplateSchema,
        SelfAuditProcess: selfAuditProcessSchema,
        AuditGet: auditGetSchema,
        AuditTemplate: auditTemplateSchema,
        AuditProcess: auditProcessSchema,
        ExtendTermGet: extendTermGetSchema,
        ExtendTermTemplate: extendTermTemplateSchema,
        ExtendTermProcess: extendTermProcessSchema,
        RenewalGet: renewalGetSchema,
        RenewalTemplate: renewalTemplateSchema,
        RenewalProcess: renewalProcessSchema,
        ReissueGet: reissueGetSchema,
        ReissueTemplate: reissueTemplateSchema,
        ReissueProcess: reissueProcessSchema,
        RewriteGet: rewriteGetSchema,
        RewriteTemplate: rewriteTemplateSchema,
        RewriteProcess: rewriteProcessSchema,
        ContractDisableGet: contractDisableGetSchema,
        ContractDisableTemplate: contractDisableTemplateSchema,
        ContractDisableProcess: contractDisableProcessSchema,
        ChangeConsumerTemplate: changeConsumerTemplateSchema,
        ChangeConsumerGet: changeConsumerGetSchema,
        ChangeConsumerProcess: changeConsumerProcessSchema,
        NonRenewTemplate: nonRenewTemplateSchema,
        NonRenewGet: nonRenewGetSchema,
        NonRenewProcess: nonRenewProcessSchema,
        ContractRateTemplate: contractRateTemplateSchema,
        ContractRateGet: contractRateGetSchema,
        ContractRateProcess: contractRateProcessSchema
    },
    {
        idAttribute: (value) => '0'
        //processStrategy: (input, parent, key) => { return addNodeTypeProcessStrategy(input, "ConceptOneReply") }
    }
);

// in root ID:0 = reply from LOAD    ID:1 = transaction being built for PROCESS
export const rootSchema = new schema.Entity(
    'Root',
    {
        ConceptOneRequest: conceptOneRequestSchema,
        ConceptOneReply: conceptOneReplySchema
    },
    { idAttribute: (value) => '0' }
);

export { normalize };
export { denormalize };
